import { useMemo } from 'react';

import { Box, Dialog, DialogContent, Grid, Typography } from '@mui/material';

import { navigate } from '@redwoodjs/router';
import { MetaTags } from '@redwoodjs/web';

import AssignPartnersModal from 'src/components/AssignPartnersModal';
import Loader from 'src/components/Loader/Loader';
import ModalContent from 'src/components/ModalContent/ModalContent';
import TableList from 'src/components/TableList/TableList';
import { DEFAULT_BORDER, PRICING_OFFER_TABLE_HEADERS } from 'src/constants';
import { PricingType } from 'src/hooks/usePricings';
import usePricingsOffers from 'src/hooks/usePricings/usePricingsOffers';
import ViewPricingModal from 'src/pages/ViewTerminalPricingsPage/ViewPricingModal';
import {
    getExportData,
    getFilterOptions,
    getTableData,
    useAssignOfferForPartners,
    useAssignOfferForPartnersOfPartner,
    useAssignPartners,
    useAssignPartnersEffect,
    useDeleteOffer,
    usePricingOffersState,
} from 'src/utils/pricingOffersUtils';
import { updateTableFilters } from 'src/utils/tableUtils';

const ViewOnlinePricingsPage = () => {
    const {
        openDeleteModal,
        setOpenDeleteModal,
        idToDelete,
        setIdToDelete,
        idToAssign,
        setIdToAssign,
        offerData,
        tableFilters,
        setTableFilters,
        assignedPartners,
        setAssignedPartners,
        assignedPartnersOfPartners,
        setAssignedPartnersOfPartners,
        useTransactionOfferId,
        getOfferData,
        loading,
        refetch,
    } = usePricingOffersState(PricingType.ONLINE);

    const [deleteOnlineOffer] = useDeleteOffer(() => {
        return window.location.reload();
    });
    const [assignOfferForPartners] = useAssignOfferForPartners(() => {
        return window.location.reload();
    });
    const [assignOfferForPartnersOfPartner] = useAssignOfferForPartnersOfPartner(() => {
        return window.location.reload();
    });

    useAssignPartnersEffect(
        idToAssign,
        offerData,
        setAssignedPartners,
        setAssignedPartnersOfPartners
    );

    const {
        assigned,
        unassigned,
        handleAssignPartners,
        handleOpenAssignModal,
        handleCloseAssignModal,
        isAssignModalOpen,
    } = useAssignPartners({
        idToAssign,
        setIdToAssign,
        assignedPartners,
        assignedPartnersOfPartners,
        refetch,
        assignOfferForPartners,
        assignOfferForPartnersOfPartner,
        useTransactionOfferId,
    });

    const filterOptions = useMemo(() => {
        return getFilterOptions(offerData);
    }, [offerData]);
    const exportData = useMemo(() => {
        return getExportData(offerData);
    }, [offerData]);
    const tableData = useMemo(() => {
        return getTableData(offerData, tableFilters, handleOpenAssignModal);
    }, [offerData, tableFilters, handleOpenAssignModal]);

    const addNewOnlineOffer = () => {
        navigate('/online-pricing/add');
    };

    const handleEditOnlineOffer = ({ id }) => {
        navigate(`/online-pricing/${id}/edit`);
    };

    // Delete record callback sent to table component
    const handleOpenDeleteModal = (recordId: number) => {
        setIdToDelete(recordId);
        setOpenDeleteModal(true);
    };

    const handleOpenViewModal = ({ id }) => {
        viewPricingOfferById(id);
        setOpenModal(true);
    };

    const handleDeleteRecord = () => {
        idToDelete && deleteOnlineOffer({ variables: { id: idToDelete } });
        handleCloseDeleteModal();
    };
    // Close the delete modal
    const handleCloseDeleteModal = () => {
        setIdToDelete(undefined);
        setOpenDeleteModal(false);
    };

    const handleCloseViewModal = () => {
        setOpenModal(false);
    };

    const { open, setOpenModal, selectedOffer, viewPricingOfferById } = usePricingsOffers(
        null,
        PricingType.ONLINE,
        false
    );

    return (
        <>
            <MetaTags title="Online Pricing" description="Online Pricing Offer Page" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid
                    item
                    xs={12}
                    sx={{
                        ...DEFAULT_BORDER,
                        height: '116px',
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                        padding: '8px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1D1D1B' }}>
                        Online Pricing Offers
                    </Typography>
                    <Typography>
                        Add, View, Remove and Update Online Pricing Offers as necessary
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    {/* Display loader if data is still fetching */}
                    {loading ? (
                        <Loader />
                    ) : (
                        <TableList
                            addRecordButtonText="Add Online Offer"
                            data={tableData}
                            exportData={exportData}
                            headers={PRICING_OFFER_TABLE_HEADERS}
                            title="Online Offer List"
                            addRecordCallBack={addNewOnlineOffer}
                            handleViewRecord={handleOpenViewModal}
                            deleteRecordCallBack={handleOpenDeleteModal}
                            handleEditRecord={handleEditOnlineOffer}
                            enableFilter
                            enableExport
                            enableEdit
                            filterOptions={filterOptions}
                            updateTableFilters={(filter, newFilterValue) => {
                                return setTableFilters((prevFilters) => {
                                    return updateTableFilters(prevFilters, newFilterValue, filter);
                                });
                            }}
                        />
                    )}
                </Grid>
                {isAssignModalOpen && (unassigned.length > 0 || assigned.length > 0) && (
                    <AssignPartnersModal
                        open={isAssignModalOpen}
                        onClose={() => {
                            handleCloseAssignModal();
                        }}
                        onAssign={(assigned) => {
                            return handleAssignPartners(assigned, []);
                        }}
                        onUnassign={(unassigned) => {
                            return handleAssignPartners([], unassigned);
                        }}
                        assignedInitial={assigned}
                        unassignedInitial={unassigned}
                    />
                )}
                <Dialog
                    open={openDeleteModal}
                    onClose={handleCloseDeleteModal}
                    aria-labelledby="delete-online-offer-dialog"
                    sx={{
                        '& .MuiDialog-paper': {
                            width: '400px',
                        },
                    }}
                >
                    <DialogContent>
                        <ModalContent
                            handleClose={handleCloseDeleteModal}
                            handleAction={handleDeleteRecord}
                            header="Delete Online Offer?"
                            message="Are you sure you want to permanently delete this online offer? This offer cannot be restored once deleted"
                            buttonText="Delete"
                        />
                    </DialogContent>
                </Dialog>
                <Box sx={{ width: '300px' }}>
                    <ViewPricingModal
                        open={open}
                        handleCloseModal={handleCloseViewModal}
                        offer={selectedOffer}
                    />
                </Box>
            </Grid>
        </>
    );
};

export default ViewOnlinePricingsPage;
